"use client"

import { useState, useMemo, useEffect, useRef } from "react"
import Link from "next/link"

interface Article {
  id: string
  title: string
  excerpt?: string
  content: string
  author_name?: string
  category?: string
  tags?: string[]
  created_at: string
  published_at?: string
  banner_image?: string
  featured_image?: string
}

interface ArticlesSearchProps {
  articles: Article[]
}

const tileBorder = "border border-[color-mix(in_oklch,var(--home-navy)_14%,transparent)]"
const tileBg = "bg-[color-mix(in_oklch,var(--home-paper-2)_45%,transparent)]"
const inputBase =
  "h-11 w-full border border-[color-mix(in_oklch,var(--home-navy)_22%,transparent)] bg-[color-mix(in_oklch,var(--home-paper-2)_35%,transparent)] px-4 text-sm text-[var(--home-ink)] outline-none transition-colors duration-200 placeholder:text-[color-mix(in_oklch,var(--home-ink)_42%,transparent)] focus:border-[var(--home-gold)]"

export function ArticlesSearch({ articles }: ArticlesSearchProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<"date" | "relevance" | "title">("date")
  const [isSearching, setIsSearching] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to clear search
      if (e.key === 'Escape' && searchTerm) {
        setSearchTerm("")
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [searchTerm])

  // Get unique categories from articles
  const categories = useMemo(() => {
    const cats = articles
      .map(article => article.category)
      .filter(Boolean)
      .filter((cat, index, arr) => arr.indexOf(cat) === index)
    return cats as string[]
  }, [articles])

  // Debounce search to improve performance
  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true)
      const timer = setTimeout(() => {
        setIsSearching(false)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setIsSearching(false)
    }
  }, [searchTerm])

  // Filter articles based on search term and category
  const filteredArticles = useMemo(() => {
    if (searchTerm === "" && selectedCategory === null) {
      return articles
    }

    return articles.filter(article => {
      const searchLower = searchTerm.toLowerCase()

      const matchesSearch = searchTerm === "" ||
        article.title.toLowerCase().includes(searchLower) ||
        article.excerpt?.toLowerCase().includes(searchLower) ||
        article.content.toLowerCase().includes(searchLower) ||
        article.author_name?.toLowerCase().includes(searchLower) ||
        article.tags?.some(tag => tag.toLowerCase().includes(searchLower))

      const matchesCategory = selectedCategory === null || article.category === selectedCategory

      return matchesSearch && matchesCategory
    }).sort((a, b) => {
      switch (sortBy) {
        case "relevance":
          if (searchTerm) {
            const aScore = getRelevanceScore(a, searchTerm)
            const bScore = getRelevanceScore(b, searchTerm)
            if (aScore !== bScore) return bScore - aScore
          }
          // Fall back to date if no search term
          const aDate = new Date(a.published_at || a.created_at)
          const bDate = new Date(b.published_at || b.created_at)
          return bDate.getTime() - aDate.getTime()

        case "title":
          return a.title.localeCompare(b.title)

        case "date":
        default:
          const aDateDefault = new Date(a.published_at || a.created_at)
          const bDateDefault = new Date(b.published_at || b.created_at)
          return bDateDefault.getTime() - aDateDefault.getTime()
      }
    })
  }, [articles, searchTerm, selectedCategory])

  // Calculate relevance score for search results
  const getRelevanceScore = (article: Article, searchTerm: string): number => {
    const searchLower = searchTerm.toLowerCase()
    let score = 0

    // Title matches are most important
    if (article.title.toLowerCase().includes(searchLower)) score += 10

    // Author matches
    if (article.author_name?.toLowerCase().includes(searchLower)) score += 5

    // Category matches
    if (article.category?.toLowerCase().includes(searchLower)) score += 3

    // Excerpt matches
    if (article.excerpt?.toLowerCase().includes(searchLower)) score += 2

    // Tag matches
    if (article.tags?.some(tag => tag.toLowerCase().includes(searchLower))) score += 3

    // Content matches (less important)
    if (article.content.toLowerCase().includes(searchLower)) score += 1

    return score
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
  }

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + "..."
  }

  // Highlight search terms in text
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-[color-mix(in_oklch,var(--home-gold)_45%,var(--home-cream))] px-1 text-inherit">
          {part}
        </mark>
      ) : part
    )
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedCategory(null)
    setSortBy("date")
  }

  const categoryButton = (active: boolean) =>
    `h-9 border px-4 text-xs font-medium uppercase tracking-[0.12em] transition-colors duration-200 ${
      active
        ? "border-[var(--home-gold)] bg-[var(--home-gold)] text-[var(--home-navy)]"
        : `${tileBorder} bg-transparent text-[var(--home-navy)] hover:border-[var(--home-gold)]`
    }`

  return (
    <div className="space-y-10">
      {/* Search and Filter Section */}
      <div className={`${tileBorder} ${tileBg} space-y-6 p-6 md:p-8`}>
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          {/* Search Input */}
          <div className="relative flex-1">
            <i
              className="fa-solid fa-magnifying-glass absolute bottom-0 left-4 top-0 my-auto h-4 w-4 text-[color-mix(in_oklch,var(--home-ink)_40%,transparent)]"
              aria-hidden
            />
            <input
              ref={searchInputRef}
              placeholder="Search articles by title, content, author, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputBase} pl-11`}
            />
          </div>

          {/* Sort Dropdown */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-2">
              <label htmlFor="article-sort" className="text-xs font-medium uppercase tracking-[0.15em] text-[color-mix(in_oklch,var(--home-ink)_55%,transparent)]">
                Sort by
              </label>
              <div className="relative">
                <select
                  id="article-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "date" | "relevance" | "title")}
                  className={`${inputBase} appearance-none pr-10`}
                >
                  <option value="date">Newest First</option>
                  <option value="relevance">Relevance</option>
                  <option value="title">Title A-Z</option>
                </select>
                <i
                  className="fa-solid fa-chevron-down pointer-events-none absolute right-4 top-0 my-auto h-3 w-3 text-[color-mix(in_oklch,var(--home-ink)_40%,transparent)]"
                  aria-hidden
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || selectedCategory || sortBy !== "date") && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex h-11 items-center gap-2 border border-[color-mix(in_oklch,var(--home-navy)_22%,transparent)] px-4 text-xs font-medium uppercase tracking-[0.15em] text-[var(--home-navy)] transition-colors duration-200 hover:border-[var(--home-gold)] hover:text-[var(--home-gold)]"
              >
                <i className="fa-solid fa-xmark" aria-hidden />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Category Filters */}
        {categories.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-[color-mix(in_oklch,var(--home-ink)_55%,transparent)]">
              Filter by category
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={categoryButton(selectedCategory === null)}
              >
                All Categories
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`${categoryButton(selectedCategory === category)} capitalize`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count and Stats */}
        <div className="flex flex-col gap-2 border-t border-[color-mix(in_oklch,var(--home-navy)_12%,transparent)] pt-5 text-sm text-[color-mix(in_oklch,var(--home-ink)_62%,transparent)] sm:flex-row sm:items-center sm:justify-between">
          <span>
            {filteredArticles.length} of {articles.length} articles
            {searchTerm && ` matching "${searchTerm}"`}
            {selectedCategory && ` in "${selectedCategory}"`}
          </span>
          {searchTerm && (
            <span className="text-xs">
              Press Escape to clear search
            </span>
          )}
        </div>
      </div>

      {/* Articles Grid */}
      {filteredArticles.length === 0 ? (
        <div className={`${tileBorder} ${tileBg} py-20 text-center`}>
          <i
            className="fa-regular fa-newspaper mb-6 text-3xl text-[color-mix(in_oklch,var(--home-navy)_30%,transparent)]"
            aria-hidden
          />

          <h3 className="font-heading text-2xl font-medium text-[var(--home-navy)]">
            {searchTerm || selectedCategory ? "No Articles Found" : "No Articles Available"}
          </h3>

          <p className="home-muted mx-auto mt-3 max-w-md text-sm leading-relaxed">
            {searchTerm || selectedCategory
              ? "Try adjusting your search terms or filters to find what you're looking for."
              : "Check back soon for insightful content and thought leadership articles."
            }
          </p>

          {(searchTerm || selectedCategory) && (
            <button
              type="button"
              onClick={clearFilters}
              className="home-btn home-btn-ghost mt-8"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {filteredArticles.map((article) => (
            <article key={article.id} className="group flex h-full flex-col border border-[color-mix(in_oklch,var(--home-navy)_14%,transparent)] bg-[color-mix(in_oklch,var(--home-paper-2)_45%,transparent)] transition-colors duration-300 hover:border-[var(--home-gold)]">
              {(article.banner_image || article.featured_image) ? (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.banner_image || article.featured_image || "/placeholder.svg"}
                    alt={article.title}
                    className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              ) : (
                <div className="flex aspect-video items-center justify-center bg-[color-mix(in_oklch,var(--home-navy)_6%,transparent)]">
                  <i
                    className="fa-regular fa-newspaper text-2xl text-[color-mix(in_oklch,var(--home-navy)_25%,transparent)]"
                    aria-hidden
                  />
                </div>
              )}

              <div className="flex flex-1 flex-col p-7 pt-6">
                {article.category && (
                  <span className="self-start border border-[color-mix(in_oklch,var(--home-gold)_45%,transparent)] px-2.5 py-1 text-[0.6rem] font-medium uppercase tracking-[0.15em] text-[var(--home-navy)] capitalize">
                    {article.category}
                  </span>
                )}

                <h3 className="mt-4 font-heading text-xl font-medium leading-snug tracking-[-0.02em] text-[var(--home-navy)] transition-colors duration-300 group-hover:text-[var(--home-gold)]">
                  {searchTerm ? highlightText(article.title, searchTerm) : article.title}
                </h3>

                <p className="home-muted mb-7 mt-3 text-sm leading-relaxed [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden">
                  {searchTerm
                    ? highlightText(truncateText(article.excerpt || article.content, 150), searchTerm)
                    : truncateText(article.excerpt || article.content, 150)
                  }
                </p>

                <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[color-mix(in_oklch,var(--home-navy)_12%,transparent)] pt-5 text-xs text-[color-mix(in_oklch,var(--home-ink)_68%,transparent)]">
                  <span className="flex min-h-[20px] items-center gap-2">
                    <i className="fa-solid fa-user w-3.5 text-[var(--home-gold)]" aria-hidden />
                    <span className="truncate">{article.author_name || "The Vantage Crest Team"}</span>
                  </span>
                  <span className="flex min-h-[20px] items-center gap-2">
                    <i className="fa-regular fa-calendar w-3.5 text-[var(--home-gold)]" aria-hidden />
                    <span>{formatDate(article.published_at || article.created_at)}</span>
                  </span>
                </div>

                <Link
                  href={`/articles/${article.id}`}
                  className="mt-6 inline-flex items-center gap-2 self-start"
                >
                  <span className="home-link text-xs font-medium uppercase tracking-[0.15em]">
                    Read Article
                  </span>
                  <i
                    className="fa-solid fa-arrow-right text-xs text-[color-mix(in_oklch,var(--home-navy)_40%,transparent)] transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-[var(--home-gold)]"
                    aria-hidden
                  />
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}